// @flow
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../../../utils/form/index';
import { FBLogin } from '../../../../../utils/facebook/index';
import { getBrowserLanguage } from '../../../../local-values/utils/countries';

import facebookLogo from '../../../../../assets/img/social/facebook.png';
import style from '../../authForm.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import formStyle from '../../../../../assets/styles/form.scss';

import type { HTTPError } from '../../../../../error/index';
import type { FormValidationResult } from '../../../../../utils/form/index';
import type { Language } from '../../../../../entities/index';
import type { FBLoginResponse } from '../../../../../utils/facebook/index';
import type { LocaleState } from '../../../../locale/duck';

type Props = {
  +fbLogin: (country: ?string, language: Language, token: string,
             email?: string, redirectTo?: string) => void;
  +login: (email: string, password: string, redirectTo?: string) => void;
  +logout: () => void;
  +isAuthing: ?boolean;
  +authError: ?HTTPError;
  +authenticated: ?boolean;
  +redirectTo?: string;
  +handleClickSignUp?: (e: Event) => void;
  +handleOpenSentForgotPasswordModal: () => void;
  +authModalVisible?: boolean;
  +authModalShowLogin?: boolean;
  +handleClose?: () => void;
  +locale: LocaleState;
};

type State = {
  email: {
    value: string;
    errors?: string[];
  };
  password: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  emailPlaceholder: {
    id: 'auth.email.placeholder',
  },
  passwordPlaceholder: {
    id: 'auth.password.placeholder',
  },
});

const initialLoginState = {
  email: {
    value: '',
  },
  password: {
    value: '',
  },
};

export default class LoginForm extends Component<Props, State> {
  formValidator: FormValidator;
  state = { ...initialLoginState };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
      password: {
        required: 'required',
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { authError } = this.props;

    if ((this.props.authModalVisible !== prevProps.authModalVisible ||
      this.props.authModalShowLogin !== prevProps.authModalShowLogin) &&
      this.props.authModalVisible && this.props.authModalShowLogin) {
      this.setState(initialLoginState);
    }

    if (authError !== prevProps.authError && !!authError) {
      if (authError.code === 401) {
        if (authError.data && authError.data.name === 'AuthenticationFailedError') {
          this.setState({
            email: {
              ...this.state.email,
              errors: (this.state.email.errors || []).concat('wrongCredentials'),
            },
            password: {
              ...this.state.password,
              errors: (this.state.password.errors || []).concat('wrongCredentials'),
            }
          });
        }
      }
    }
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length && (errors[0] === 'emailFormat')) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleClickForgotPassword(e: Event) {
    e.preventDefault();
    const { handleOpenSentForgotPasswordModal } = this.props;
    handleOpenSentForgotPasswordModal();
  }

  handleLoginFB(e: Event) {
    const { fbLogin, redirectTo, locale } = this.props;
    FBLogin(window).then((response: FBLoginResponse) => {
      if (response.status === 'connected' && response.authResponse) {
        const localeUpper = locale.locale ? locale.locale.toUpperCase() : '';
        const lang = localeUpper || getBrowserLanguage();
        fbLogin(locale.country, lang, response.authResponse.accessToken, undefined, redirectTo);
      }
    });
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { login, redirectTo } = this.props;
    const emailValue = this.state.email.value;
    const passwordValue = this.state.password.value;
    const errors = this.formValidator.validateForm({
      email: emailValue,
      password: passwordValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else {
      login(emailValue, passwordValue, redirectTo);
    }
  }

  render() {
    const { isAuthing, authError, handleClickSignUp, handleClose } = this.props;
    const { email, password } = this.state;
    const passwordError = password.errors && FormValidator.buildErrorLabel(password.errors, {
      required: 'auth.password.error.required',
    });
    const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
      emailFormat: 'auth.email.error.format',
      required: 'auth.email.error.required',
    });
    let error;
    if (authError) {
      if (authError.code === 401 && authError.data && authError.data.name === 'AuthenticationFailedError') {
        error = <FormattedMessage id="auth.error.wrongCredentials">{
          txt => (<p className={classNames(style.text, style.red, style.lowerError)}>{txt}</p>)
        }</FormattedMessage>;
      }
      if (authError.code === 429) {
        error = <FormattedMessage id="httpErrors.post.429">{
          txt => (<p className={classNames(style.text, style.red, style.lowerError)}>{txt}</p>)
        }</FormattedMessage>;
      }
      if (Math.round(authError.code / 100) === 5) {
        error = <FormattedMessage id="auth.error.httpError">{
          txt => (<p className={classNames(style.text, style.red, style.lowerError)}>{txt}</p>)
        }</FormattedMessage>;
      }
    }

    return (
      <form className={classNames(style.authForm, formStyle.formWrap)}
            onSubmit={this.handleSubmit.bind(this)}
            method="POST">
        <FormattedMessage id="auth.title">{txt => <h1>{txt}</h1>}</FormattedMessage>
        <FormattedMessage id="auth.facebook.button">{
          txt => (<button type="button"
                          disabled={isAuthing}
                          onClick={this.handleLoginFB.bind(this)}
                          className={classNames(buttonStyle.socialBtn, buttonStyle.facebook)}>
            <img src={facebookLogo} alt="facebook"/>
            <span className={style.fbLogin}>{txt}</span>
          </button>)
        }</FormattedMessage>
        <div className={style.separator}>
          <hr className={style.line}/>
          <p className={style.or}>o</p>
          <hr className={style.line}/>
        </div>
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
          <FormattedMessage id="auth.email.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <input type="email"
                 className={classNames(inputStyle.input,
                   { [inputStyle.red]: email.errors && email.errors.length },
                   { [inputStyle.lightBlue]: !!email.value })}
                 onChange={this.handleChange.bind(this)}
                 onFocus={this.handleFocus.bind(this)}
                 onBlur={this.handleBlur.bind(this)}
                 disabled={isAuthing}
                 name="email"
                 value={email.value}
                 placeholder={this.context.intl.formatMessage(messages.emailPlaceholder)}/>
          {emailError}
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow)}>
          <FormattedMessage id="auth.password.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <input type="password"
                 className={classNames(inputStyle.input,
                   { [inputStyle.red]: password.errors && password.errors.length },
                   { [inputStyle.lightBlue]: !!password.value })}
                 onChange={this.handleChange.bind(this)}
                 onFocus={this.handleFocus.bind(this)}
                 disabled={isAuthing}
                 name="password"
                 value={password.value}
                 placeholder={this.context.intl.formatMessage(messages.passwordPlaceholder)}/>
          {passwordError}
        </section>
        {error}
        <FormattedMessage id="auth.button">{
          txt => (
            <button
              type="submit"
              disabled={isAuthing}
              className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.submitBtn)}
              onClick={this.handleSubmit.bind(this)}>{txt}
            </button>
          )
        }</FormattedMessage>
        <FormattedMessage id="auth.notRegistered">{
          txt => (<p className={classNames(style.text, style.separated)}>{txt}
            <FormattedMessage id="auth.registerHere">{
              t => <Link onClick={handleClickSignUp} to="/sign-up">{t}</Link>
            }</FormattedMessage>.</p>)
        }</FormattedMessage>
        <FormattedMessage id="auth.forgotPassword">{
          txt => (<a onClick={this.handleClickForgotPassword.bind(this)} className={style.separated}>{txt}</a>)
        }</FormattedMessage>
        <label>
          <FormattedMessage id="auth.agree">{txt => (<span>{txt} </span>)}</FormattedMessage>
          <FormattedMessage id="auth.terms">{txt => (
            <Link to="/terms-of-service"
                  onClick={() => !!handleClose && handleClose()}>{txt}</Link>)}</FormattedMessage>
          <FormattedMessage id="auth.and">{txt => <span> {txt} </span>}</FormattedMessage>
          <FormattedMessage id="auth.policy">{txt => (
            <Link to="/privacy-policy"
                  onClick={() => !!handleClose && handleClose()}>{txt}</Link>)}</FormattedMessage>
          .
        </label>

      </form>
    );
  }
}

LoginForm.propTypes = {
  login: PropTypes.func.isRequired,
  fbLogin: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  isAuthing: PropTypes.bool,
  authError: PropTypes.object,
  authenticated: PropTypes.bool,
  redirectTo: PropTypes.string,
  handleClickSignUp: PropTypes.func,
  handleOpenSentForgotPasswordModal: PropTypes.func.isRequired,
  authModalVisible: PropTypes.bool,
  authModalShowLogin: PropTypes.bool,
  handleClose: PropTypes.func,
  locale: PropTypes.object,
};

LoginForm.contextTypes = {
  intl: intlShape,
};




// WEBPACK FOOTER //
// ./src/modules/auth/components/Login/LoginForm/LoginForm.jsx